import { join } from "node:path";
import type { DriftIssue } from "../../types.js";
import { isLearnEnabled, readPrompts } from "../../learn/recorder.js";
import { detectCorrections, clusterCorrections } from "../../learn/corrections.js";
import { dedupeCorrections, toCorrectionEntries } from "../../learn/dedupe.js";

/** Minimum cluster count before we surface a drift issue. */
const MIN_CLUSTER_COUNT = 3;

/**
 * Surfaces recurring correction patterns as drift issues.
 *
 * Pipeline:
 *   1. detectCorrections() — flag prompts that look like corrections
 *   2. dedupeCorrections() — fold near-duplicate phrasing together
 *      so the same fact corrected 5 different ways counts once
 *   3. clusterCorrections() — group by exact normalized text, count
 *   4. filter by ≥ MIN_CLUSTER_COUNT and emit issues
 *
 * Only runs when `cai learn` is enabled — no log means no issues.
 */
export function checkCorrectionClusters(projectRoot: string, scaffoldRoot: string): DriftIssue[] {
  if (!isLearnEnabled(projectRoot)) return [];

  const entries = readPrompts(projectRoot);
  if (entries.length === 0) return [];

  const corrections = detectCorrections(entries);
  if (corrections.length === 0) return [];

  // Semantic dedupe: fold near-duplicates before counting so the same fact
  // corrected 5 different ways counts once.
  const dedupedEntries = dedupeCorrections(toCorrectionEntries(corrections));

  // Re-derive Correction objects from the deduped set so downstream
  // clustering sees one entry per near-duplicate group.
  const dedupedCorrections = dedupedEntries.map((e) => e.source);

  const clusters = clusterCorrections(dedupedCorrections).filter((c) => c.count >= MIN_CLUSTER_COUNT);

  return clusters.map((cluster) => ({
    code: "RECURRING_CORRECTION" as const,
    severity: "warning" as const,
    file: join(scaffoldRoot, "CLAUDE.md").replace(projectRoot + "/", ""),
    line: null,
    message: `Recurring correction (${cluster.count}×): "${cluster.example}"`,
    suggestion: `Add a rule to CLAUDE.md to prevent this: \`cai learn write-rule ${cluster.id}\``,
  }));
}
